import React, {useEffect, useRef, useState} from 'react';
import {App, Button, Modal, QRCode} from 'antd';

import {
    createSubscriptionOrder,
    getOrderStatus,
    type BillingCycleType,
    type BillingProductType,
    type ICreateOrderData
} from '@/api/billing';

// 付费协议链接
const AGREEMENT_URL = 'https://cloud.baidu.com/doc/FAMOU/s/rmrsq2ne9';
const AGREEMENT_TEXT = '《百度伐谋付费协议(含自动续费条款)》';
// 轮询间隔（ms）
const POLL_INTERVAL = 5000;
// 支付成功后自动关闭延时（ms）
const SUCCESS_CLOSE_DELAY = 1500;
const QR_BOX = 200;
// 对齐 Figma image 4（186px 满铺）：antd QRCode bordered=false 无 quiet zone，黑块会顶到画布边缘，
// 故取略小于白框的尺寸并居中，四周留白 =(200-176)/2 = 12px，既贴近 Figma 满铺又不让角贴住边框。
const QR_SIZE = 176;

// 深色主题色值（workspace 基准为黑色）
const CARD_BG = '#222426';
const CARD_BORDER = '#2B2D31';
const QR_BORDER = '#32363C';
const OVERLAY_BG = 'rgba(0, 0, 0, 0.6)';
const OVERLAY_TEXT = '#EBEBEB';
const TITLE_COLOR = '#B3B3B3';
const ALIPAY_TEXT = '#818999';
const LINK_COLOR = '#6C8CFF';

// 支付页状态：协议遮罩 → 扫码 → 支付成功 / 链接失效 / 支付失败
type PayState = 'agreement' | 'qr' | 'success' | 'expired' | 'failed';

interface IPaymentQRModalProps {
    open: boolean;
    /** CreateOrder 返回的订单号，用于轮询订单状态 */
    orderId: string;
    /** 商品 ID，用于「重新获取」时重新下单 */
    goodsId: string;
    /** 订单类型（订阅套餐必传） */
    orderType?: BillingCycleType;
    /** 产品线，默认 ws */
    product?: BillingProductType;
    /** 购买数量（资源点包等按数量下单场景传入，默认 1；用于「重新获取」时重新下单） */
    quantity?: number;
    /** 首次下单返回的支付地址（支付宝扫码地址） */
    payUrl: string;
    /** 价格展示，如 "¥29.9" 或 "29.9" */
    price?: string;
    onClose: () => void;
    /** 支付成功回调（关闭弹窗并刷新账户信息） */
    onPaid?: () => void;
}

const AlipayIcon: React.FC = () => (
    // eslint-disable-next-line max-len
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0_alipay)"><path d="M18 12.3228V3.4605C17.9995 2.54271 17.6347 1.66266 16.9856 1.01377C16.3366 0.364874 15.4565 0.000238449 14.5388 0L3.46033 0C2.54274 0.000476942 1.66287 0.365218 1.01403 1.01408C0.365199 1.66295 0.000476918 2.54286 0 3.4605V14.5395C0.000238571 15.4572 0.364884 16.3373 1.01377 16.9862C1.66266 17.6351 2.54266 17.9998 3.46033 18H14.5388C15.3505 17.9994 16.1363 17.7138 16.759 17.1931C17.3817 16.6723 17.8018 15.9495 17.946 15.1506C17.028 14.7528 13.0502 13.0356 10.9777 12.0456C9.40093 13.9563 7.74861 15.1029 5.25934 15.1029C2.77006 15.1029 1.10784 13.5693 1.30763 11.6919C1.43903 10.4607 2.28409 8.4474 5.9532 8.7921C7.88811 8.9739 8.77276 9.3348 10.3504 9.8559C10.7581 9.1071 11.0973 8.2836 11.3547 7.4079H4.36028V6.7149H7.82151V5.4693H3.59982V4.707H7.82061V2.9115C7.82061 2.9115 7.85841 2.6307 8.16889 2.6307H9.89951V4.707H14.3993V5.4702H9.89951V6.714H13.5704C13.2538 8.02482 12.7517 9.28372 12.0792 10.4526C13.1456 10.8396 17.9991 12.3228 17.9991 12.3228H18ZM4.98395 13.9149C2.35338 13.9149 1.9376 12.2544 2.0771 11.5605C2.21479 10.8693 2.97705 9.9675 4.43948 9.9675C6.11969 9.9675 7.62532 10.3977 9.43243 11.2779C8.16349 12.9303 6.60387 13.9149 4.98395 13.9149Z" fill="#0075FF" /></g><defs><clipPath id="clip0_alipay"><rect width="18" height="18" rx="9" fill="white" /></clipPath></defs></svg>
);

const CheckIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const CloseMarkIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const RefreshIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);

const CloseBtnIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B3B3B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// 深色主题主按钮：浅色底、黑色字
const lightBtnStyle: React.CSSProperties = {
    height: 32,
    padding: '6px 16px',
    borderRadius: 8,
    background: '#FAFAFA',
    borderColor: '#FAFAFA',
    color: '#000000',
    fontSize: 13,
    fontWeight: 500,
    boxShadow: '0px 1px 2px rgba(24, 24, 27, 0.15), 0px 2px 4px rgba(24, 24, 27, 0.07)'
};

const PaymentQRModal: React.FC<IPaymentQRModalProps> = ({
    open,
    orderId,
    goodsId,
    orderType,
    product,
    quantity = 1,
    payUrl,
    price,
    onClose,
    onPaid
}) => {
    const {message} = App.useApp();
    const [state, setState] = useState<PayState>('agreement');
    const [currentOrderId, setCurrentOrderId] = useState(orderId);
    const [currentPayUrl, setCurrentPayUrl] = useState(payUrl);
    const [refreshing, setRefreshing] = useState(false);
    const timerRef = useRef<number | null>(null);

    // 弹窗打开或订单更新时回到协议态
    useEffect(() => {
        if (open) {
            setState('agreement');
            setCurrentOrderId(orderId);
            setCurrentPayUrl(payUrl);
        }
    }, [open, orderId, payUrl]);

    // 仅在扫码态轮询订单状态
    useEffect(() => {
        if (!open || state !== 'qr' || !currentOrderId) {
            return;
        }
        let cancelled = false;

        const poll = async () => {
            try {
                const res = await getOrderStatus({orderId: currentOrderId});
                const data = res.data;
                if (cancelled || !data) {
                    return;
                }
                if (data.pay_url) {
                    setCurrentPayUrl(prev => (data.pay_url && data.pay_url !== prev ? data.pay_url : prev));
                }
                if (data.paid || data.pay_status === 'paid') {
                    setState('success');
                    return;
                }
                if (data.pay_status === 'failed') {
                    setState('failed');
                    return;
                }
                if (data.expired || data.pay_status === 'expired') {
                    setState('expired');
                    return;
                }
            }
            catch {
                // 轮询期间的瞬时错误忽略，继续下一轮
            }
            if (!cancelled) {
                timerRef.current = window.setTimeout(poll, POLL_INTERVAL);
            }
        };

        timerRef.current = window.setTimeout(poll, POLL_INTERVAL);

        return () => {
            cancelled = true;
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [open, state, currentOrderId]);

    // 支付成功：顶部提示 + 延时关闭并刷新
    useEffect(() => {
        if (state !== 'success') {
            return;
        }
        message.success('支付成功');
        const t = window.setTimeout(() => onPaid?.(), SUCCESS_CLOSE_DELAY);
        return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // 支付失败：顶部错误提示
    useEffect(() => {
        if (state !== 'failed') {
            return;
        }
        message.error('支付失败，请重试');
    }, [state, message]);

    // 链接失效/失败后「重试」：重新下单拿新订单号与支付地址
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const res = await createSubscriptionOrder({goods_id: goodsId, order_type: orderType, product, quantity});
            const data = res.data as ICreateOrderData | undefined;
            if (data?.pay_url) {
                setCurrentOrderId(data.order_id);
                setCurrentPayUrl(data.pay_url);
                setState('qr');
            }
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : '重新获取失败，请重试');
        }
        finally {
            setRefreshing(false);
        }
    };

    const priceNum = (price ?? '').replace(/^¥\s*/, '');

    const renderQrBox = (overlay?: React.ReactNode) => (
        <div
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: QR_BOX,
                height: QR_BOX,
                background: '#FFFFFF',
                border: `0.5px solid ${QR_BORDER}`,
                borderRadius: 14.2857,
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.2)'
            }}
        >
            <QRCode
                value={currentPayUrl}
                size={QR_SIZE}
                bordered={false}
                color="#000000"
                bgColor="#FFFFFF"
            />
            {overlay && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 16,
                        padding: 10,
                        borderRadius: 14.2857,
                        background: OVERLAY_BG,
                        backdropFilter: 'blur(7.5px)'
                    }}
                >
                    {overlay}
                </div>
            )}
        </div>
    );

    const agreementLink = (
        <a href={AGREEMENT_URL} target="_blank" rel="noopener noreferrer" style={{color: LINK_COLOR}}>
            {AGREEMENT_TEXT}
        </a>
    );

    const renderCenter = () => {
        if (state === 'agreement') {
            return renderQrBox(
                <>
                    <div style={{textAlign: 'center', fontSize: 12, lineHeight: '18px', color: OVERLAY_TEXT}}>
                        支付前请阅读
                        <br />
                        {agreementLink}
                    </div>
                    <Button type="primary" style={lightBtnStyle} onClick={() => setState('qr')}>
                        同意并支付
                    </Button>
                </>
            );
        }

        if (state === 'success') {
            return renderQrBox(
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: '#13B982'
                        }}
                    >
                        <CheckIcon />
                    </div>
                    <div style={{fontSize: 16, fontWeight: 500, color: OVERLAY_TEXT}}>支付成功</div>
                </>
            );
        }

        if (state === 'failed') {
            return renderQrBox(
                <>
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8}}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: '#FA423C'
                            }}
                        >
                            <CloseMarkIcon />
                        </div>
                        <div style={{fontSize: 13, fontWeight: 500, color: OVERLAY_TEXT}}>支付失败，请重试</div>
                    </div>
                    <Button type="primary" loading={refreshing} style={lightBtnStyle} onClick={handleRefresh}>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                            <RefreshIcon />
                            请重试
                        </span>
                    </Button>
                </>
            );
        }

        if (state === 'expired') {
            return renderQrBox(
                <>
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8}}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.12)',
                                color: OVERLAY_TEXT,
                                fontSize: 15,
                                fontWeight: 600
                            }}
                        >
                            !
                        </div>
                        <div style={{fontSize: 13, fontWeight: 500, color: OVERLAY_TEXT}}>二维码已失效</div>
                    </div>
                    <Button type="primary" loading={refreshing} style={lightBtnStyle} onClick={handleRefresh}>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                            <RefreshIcon />
                            重新获取
                        </span>
                    </Button>
                </>
            );
        }

        return renderQrBox();
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={395}
            centered
            closeIcon={<CloseBtnIcon />}
            styles={{
                content: {
                    padding: 0,
                    borderRadius: 12,
                    background: CARD_BG,
                    border: `0.5px solid ${CARD_BORDER}`,
                    boxShadow: '0px 2px 20px rgba(0, 0, 0, 0.4)',
                    overflow: 'hidden'
                },
                body: {padding: 0}
            }}
        >
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 24,
                    padding: 48
                }}
            >
                {/* 标题 + 价格 */}
                <div style={{display: 'flex', alignItems: 'flex-end', gap: 8}}>
                    <span style={{fontSize: 14, fontWeight: 500, lineHeight: '22px', color: TITLE_COLOR}}>扫码支付</span>
                    {priceNum && (
                        <span style={{display: 'flex', alignItems: 'flex-end', gap: 2, color: '#FF713D'}}>
                            <span style={{fontSize: 16, fontWeight: 500, lineHeight: '24px'}}>¥</span>
                            <span style={{fontSize: 36, fontWeight: 700, lineHeight: '36px', fontFamily: "'DIN Alternate', 'PingFang SC', sans-serif"}}>
                                {priceNum}
                            </span>
                        </span>
                    )}
                </div>

                {/* 中心内容 */}
                {renderCenter()}

                {/* 支付宝扫码支付 */}
                <div style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: ALIPAY_TEXT}}>
                    <AlipayIcon />
                    <span>支付宝扫码支付</span>
                </div>

                {/* 底部协议 */}
                {state !== 'agreement' && (
                    <div style={{fontFamily: "'PingFang SC'", fontSize: 13, fontWeight: 400, lineHeight: '20px', color: '#666666'}}>
                        您已同意{agreementLink}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PaymentQRModal;
